import { createTheme } from '@material-ui/core/styles';

const fontTakeawaySans = {
  fontFamily: 'Takeaway Sans',
  fontStyle: 'normal',
  fontWeight: 400,
  src: `url('https://assets.scoober.com/food-tracker/fonts/TakeawaySans-Regular.ttf') format('truetype')`,
};

const fontTakeawaySansBold = {
  fontFamily: 'Takeaway Sans',
  fontStyle: 'normal',
  fontWeight: 700,
  src: `url('https://assets.scoober.com/food-tracker/fonts/TakeawaySans-Bold.ttf') format('truetype')`,
};

export const scooberPalette = {
  darkBlue: '#1574f5',
  blue: '#50AADD',
  lightBlue: 'rgba(21, 116, 245, 0.1)',
  orange: '#FF8000',
  red: '#FF4246',
  lightRed: 'rgba(255,0,0 , 0.5)',
  purple: '#6B54DB',
  green: '#43a047',
  lightGreen: '#00CC33',
  white: '#FFFFFF',
  lightGrey: '#F5F6F9',
  darkGrey: '#454649',
  grey: '#D7D7D7',
  blueGreen: '#0a3847',
  cream: '#F8F5F2',
  coldDark: '#666666',
  yellow: '#FFECB3',
  coldVeryLight: '#EBEBEB',
  errorRed: '#eb0004',
  infoBlue: '#205A6D',
};

export const scooberFonts = {
  primary: 'Takeaway Sans, Roboto, sans-serif',
  secondary: 'Open Sans, sans-serif',
};

const defaultTheme = createTheme({
  breakpoints: {
    values: {
      xs: 480,
      sm: 760,
      md: 992,
      lg: 1280,
      xl: 1920,
    },
  },
  variables:{
    spacingE:'24px',
    spacingA:'4px',
    spacingC: '12px'
  },
  palette: {
    jet:{
      supportBrand02:'#ffead4'
    },
    background: {
      default: '#FCFCFC'
    },
    primary: {
      contrastText: scooberPalette.white,
      main: scooberPalette.darkBlue,
      dark: scooberPalette.coldDark,
    },
    secondary: {
      main: scooberPalette.orange,
      dark: scooberPalette.grey,
      light: scooberPalette.white,
      contrastText: scooberPalette.blueGreen,
    },
    success: {
      main: '#43a047',
      light: '#0ca74f',
    },
    warning: {
      main: '#ef6c00',
      light: '#ffecb3',
    },
    error: {
      main: '#eb0004',
    },
    information: {
      main: scooberPalette.infoBlue
    },
    tab: {
      main: '#756e68',
    },
    highlight: {
      main: '#ca2002',
    },
    border: {
      main: '',
      light: scooberPalette.coldVeryLight,
    }
  },
  typography: {
    fontFamily: scooberFonts.primary,
    body1: {
      fontSize: '.875rem',
      lineHeight: '1.25rem',
    },
    body2: {
      fontSize: '.750rem',
      lineHeight: '1rem',
    },
    subtitle1: {
      fontSize: '1.0625rem',
    },
    caption: {
      fontFamily: scooberFonts.secondary,
      fontSize: '.875rem',
      fontWeight: 400,
      lineHeight: 1.29,
    },
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: '1.25',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: '2rem',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: '1.2',
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: '1.25em',
    },
    h5: {
      fontFamily: scooberFonts.secondary,
      fontSize: '.875rem',
      fontWeight: 600,
      lineHeight: 1.29,
    },
    h6: {
      fontFamily: scooberFonts.secondary,
      fontSize: '.75rem',
      fontWeight: 700,
      lineHeight: 1.75,
    },
  },
  content: {
    width: '100%',
    maxWidth: '1100px',
  },
  fonts: {
    primary: scooberFonts.primary,
    secondary: scooberFonts.secondary,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '@font-face': [fontTakeawaySans, fontTakeawaySansBold],
        body: {
          minWidth: '320px',
          color: scooberPalette.blueGreen,
        },
      },
    },
    MuiInputLabel: {
      root: {
        fontSize: '1rem',
        '&.Mui-focused, &.Mui-error': {
          color: '#717171',
        },
      },
    },
    MuiFilledInput: {
      root: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        backgroundColor: `${scooberPalette.white} !important`,
        '& .MuiFilledInput-input': {
          borderRadius: '2px',
          border: `solid 1px ${scooberPalette.grey}`,
          padding: '27px 13px 10px',
        },
        '&.MuiInputBase-adornedEnd': {
          border: '1px solid #d7d7d7',
          '&.Mui-focused': {
            border: '2px solid #1574f5',
          },
          '& .MuiFilledInput-input': {
            border: '0',
          },
        },
        '&.Mui-error': {
          '&.MuiInputBase-adornedEnd': {
            border: `2px solid ${scooberPalette.errorRed}`,
            '&.Mui-focused': {
              border: `2px solid ${scooberPalette.errorRed}`,
            },
            '& .MuiFilledInput-input': {
              border: '0',
            },
          },
          '& .MuiFilledInput-input': {
            border: `solid 2px ${scooberPalette.errorRed}`,
            padding: '26px 12px 9px',
          },
        },
        '&.Mui-focused': {
          '& .MuiFilledInput-input': {
            border: `solid 2px ${scooberPalette.darkBlue}`,
            padding: '26px 12px 9px',
          },
        },
      },
      underline: {
        '&&&:before': {
          borderBottom: 'none',
        },
        '&&:after': {
          borderBottom: 'none',
        },
      },
    },
    MuiFormHelperText: {
      root: {
        '&.MuiFormHelperText-contained': {
          marginLeft: '0',
        },
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: 'none',
      },
      sizeSmall: {
        padding: `12px 16px`,
      },
    },
    MuiCheckbox: {
      colorSecondary: {
        color: 'transparent',
        '&$checked': {
          color: scooberPalette.orange,
          fontSize: '12px',
        },
      },
    },
  },
});

export default defaultTheme;
